import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { getImageUrl } from '../utils/imageUtils';
import { 
  FolderPlus, 
  Lock, 
  Unlock, 
  Upload, 
  X, 
  Edit2, 
  Trash2, 
  Image, 
  ChevronLeft,
  Plus,
  MoreVertical,
  Video,
  Play,
  CheckCircle,
  AlertCircle,
  FileImage,
  FileVideo,
  Eye,
  Heart,
  UserCircle,
  Globe,
  ImageIcon,
  Star
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';

export default function AlbumsManager() {
  const { api, refreshUser } = useAuth();
  const [albums, setAlbums] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const publicFileInputRef = useRef(null);
  const privateFileInputRef = useRef(null);
  
  // Selected media for viewing
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [selectedPhotoType, setSelectedPhotoType] = useState('public');
  const videoRef = useRef(null);
  
  // Form states
  const [newAlbumName, setNewAlbumName] = useState('');
  const [newAlbumPrivate, setNewAlbumPrivate] = useState(false);
  const [editName, setEditName] = useState('');
  const [editPrivate, setEditPrivate] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [albumsRes, profileRes] = await Promise.all([
        api.get('/albums'),
        api.get('/profile')
      ]);
      setAlbums(albumsRes.data);
      setProfile(profileRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlbumDetails = async (albumId) => {
    try {
      const response = await api.get(`/albums/${albumId}`);
      setSelectedAlbum(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de l\'album');
    }
  };

  const createAlbum = async () => {
    if (!newAlbumName.trim()) {
      toast.error('Veuillez entrer un nom pour l\'album');
      return;
    }

    try {
      const response = await api.post('/albums', {
        name: newAlbumName,
        is_private: newAlbumPrivate
      });
      setAlbums([response.data, ...albums]);
      setShowCreateModal(false);
      setNewAlbumName('');
      setNewAlbumPrivate(false);
      toast.success('Album créé avec succès');
    } catch (error) {
      toast.error('Erreur lors de la création de l\'album');
    }
  };

  const updateAlbum = async () => {
    if (!selectedAlbum || !editName.trim()) return;

    try {
      const response = await api.put(`/albums/${selectedAlbum.id}`, {
        name: editName,
        is_private: editPrivate
      });
      
      setAlbums(albums.map(a => a.id === selectedAlbum.id ? response.data : a));
      setSelectedAlbum(response.data);
      setShowEditModal(false);
      toast.success('Album mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteAlbum = async (albumId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet album et tous ses médias ?')) return;

    try {
      await api.delete(`/albums/${albumId}`);
      setAlbums(albums.filter(a => a.id !== albumId));
      if (selectedAlbum?.id === albumId) {
        setSelectedAlbum(null);
      }
      toast.success('Album supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Upload photo to profile (public or private)
  const handlePhotoUpload = async (e, isPrivate = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_private', isPrivate.toString());

    try {
      await api.post('/photos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo ajoutée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  // Upload video to profile (public or private)
  const handleVideoUpload = async (e, isPrivate = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 1024 * 1024 * 1024) {
      toast.error('La vidéo ne doit pas dépasser 1024 Mo');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('is_private', isPrivate.toString());

    try {
      await api.post('/videos/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Vidéo ajoutée');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const setAsProfilePicture = async (photoId, photoUrl) => {
    try {
      await api.put('/photos/set-profile-picture', { photo_id: photoId, photo_url: photoUrl });
      toast.success('Photo de profil mise à jour');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Delete photo from profile (public or private)
  const deletePhoto = async (photoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette photo ?')) return;
    
    try {
      await api.delete(`/photos/${photoId}`);
      toast.success('Photo supprimée');
      fetchData();
      refreshUser();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Delete video from profile (public or private)
  const deleteVideo = async (videoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette vidéo ?')) return;
    
    try {
      await api.delete(`/videos/${videoId}`);
      toast.success('Vidéo supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Unified media upload function for albums
  const uploadMedia = async (files) => {
    if (!files || files.length === 0 || !selectedAlbum) return;

    setUploading(true);
    setUploadProgress(0);
    
    const totalFiles = files.length;
    let uploadedCount = 0;

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);

      try {
        await api.post(`/albums/${selectedAlbum.id}/media`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        uploadedCount++;
        setUploadProgress(Math.round((uploadedCount / totalFiles) * 100));
      } catch (error) {
        toast.error(`Erreur: ${file.name} - ${error.response?.data?.detail || 'Upload échoué'}`);
      }
    }

    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} média(s) ajouté(s)`);
      fetchAlbumDetails(selectedAlbum.id);
    }
    
    setUploading(false);
    setUploadProgress(0);
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      uploadMedia(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragEnter = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length > 0) {
      uploadMedia(files);
    }
  }, [selectedAlbum]);

  const removeMedia = async (mediaId) => {
    if (!selectedAlbum) return;
    if (!window.confirm('Supprimer ce média ?')) return;

    try {
      await api.delete(`/albums/${selectedAlbum.id}/media/${mediaId}`);
      toast.success('Média supprimé');
      fetchAlbumDetails(selectedAlbum.id);
      setSelectedMedia(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const setAlbumCover = async (mediaId, mediaUrl) => {
    if (!selectedAlbum) return;

    try {
      const response = await api.put(`/albums/${selectedAlbum.id}/cover`, { media_id: mediaId });
      toast.success('Couverture de l\'album mise à jour');
      // Use the cover_url from backend response (may be a thumbnail for videos)
      const newCoverUrl = response.data.cover_url || mediaUrl;
      // Update local state
      setSelectedAlbum(prev => ({ ...prev, cover_url: newCoverUrl }));
      // Also update albums list
      setAlbums(albums.map(a => 
        a.id === selectedAlbum.id ? { ...a, cover_url: newCoverUrl } : a
      ));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour de la couverture');
    }
  };

  const openEditModal = (album) => {
    setEditName(album.name);
    setEditPrivate(album.is_private);
    setShowEditModal(true);
  };

  const isVideo = (media) => {
    return media.type === 'video' || media.url?.match(/\.(mp4|webm|mov|avi|mkv|m4v|mpeg|ogg|wmv)$/i);
  };

  const openMediaModal = (media) => {
    setSelectedMedia(media);
  };

  const closeMediaModal = () => {
    setSelectedMedia(null);
    if (videoRef.current) {
      videoRef.current.pause();
    }
  };

  // Get albums by visibility
  const publicAlbums = albums.filter(a => !a.is_private);
  const privateAlbums = albums.filter(a => a.is_private);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // Album detail view with media grid and upload zone
  if (selectedAlbum) {
    const allMedia = [...(selectedAlbum.media || []), ...(selectedAlbum.photos || []).map(p => ({...p, type: 'photo'}))];
    
    return (
      <div data-testid="album-detail-view">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => setSelectedAlbum(null)}
              className="text-gray-400 hover:text-white"
              data-testid="back-to-albums-btn"
            >
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div>
              <h2 className="font-serif text-xl text-white flex items-center gap-2">
                {selectedAlbum.name}
                {selectedAlbum.is_private ? (
                  <Lock className="w-4 h-4 text-gold" />
                ) : (
                  <Unlock className="w-4 h-4 text-green-500" />
                )}
              </h2>
              <p className="text-sm text-gray-500">
                {allMedia.length} média(s) • {selectedAlbum.is_private ? 'Album privé' : 'Album public'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => openEditModal(selectedAlbum)}
              className="btn-outline-gold"
            >
              <Edit2 className="w-4 h-4 mr-2" /> Modifier
            </Button>
          </div>
        </div>

        {/* Upload Zone with Criteria */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* File Criteria */}
          <div className="glass-card p-6">
            <h3 className="font-semibold text-white mb-4">Vos fichiers doivent respecter les critères suivants</h3>
            <ul className="space-y-3 text-sm text-gray-300">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Image acceptée: .jpg, .jpeg, .png, .heic, .heif</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Taille minimum de l'image: 200x200 px</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>10 Mo maximum par image</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>Vidéo acceptée: .mp4, .avi, .mpeg, .mov, .ogg, .wmv, .webm</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                <span>1024 Mo maximum par vidéo</span>
              </li>
            </ul>
            <div className="mt-4 pt-4 border-t border-white/10">
              <p className="text-xs text-gold flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Assurez-vous que vos médias respectent les Conditions Générales d'Utilisation
              </p>
            </div>
          </div>

          {/* Drop Zone */}
          <div
            className={`glass-card p-8 flex flex-col items-center justify-center min-h-[280px] transition-all cursor-pointer ${
              isDragging 
                ? 'border-2 border-gold border-dashed bg-gold/10' 
                : 'border-2 border-dashed border-white/20 hover:border-gold/50'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="media-drop-zone"
          >
            {uploading ? (
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gold font-semibold">Upload en cours... {uploadProgress}%</p>
              </div>
            ) : (
              <>
                <div className="flex flex-col items-center mb-4">
                  <FileImage className="w-10 h-10 text-gray-400 mb-2" />
                  <Upload className="w-8 h-8 text-gray-500" />
                  <FileVideo className="w-10 h-10 text-gray-400 mt-2" />
                </div>
                <p className="text-gray-300 text-center font-serif text-lg mb-2">
                  Glissez déposez vos médias ou
                </p>
                <p className="text-gray-400 text-center mb-4">
                  sélectionnez les depuis votre ordinateur
                </p>
                <Button 
                  className="bg-red-500 hover:bg-red-600 text-white px-6"
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Sélectionnez vos médias à importer
                </Button>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>

        {/* Media Grid */}
        {allMedia.length > 0 && (
          <div>
            <h3 className="text-gold text-sm uppercase tracking-widest mb-4">
              Médias de l'album ({allMedia.length})
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {allMedia.map((media, index) => {
                const isCover = selectedAlbum.cover_url === media.url;
                return (
                  <div 
                    key={media.id || index}
                    className={`aspect-square relative group cursor-pointer rounded-sm overflow-hidden bg-dark-subtle ${isCover ? 'ring-2 ring-gold' : ''}`}
                    onClick={() => openMediaModal(media)}
                  >
                    {isVideo(media) ? (
                      <>
                        <video
                          src={`${getImageUrl(media.url)}`}
                          className="w-full h-full object-cover"
                          muted
                          preload="metadata"
                        />
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                        <div className="absolute top-2 left-2">
                          <Video className="w-4 h-4 text-gold" />
                        </div>
                      </>
                    ) : (
                      <img
                        src={`${getImageUrl(media.url)}`}
                        alt=""
                        className="w-full h-full object-cover"
                      />
                    )}
                    
                    {/* Cover badge */}
                    {isCover && (
                      <span className="absolute top-2 right-2 px-2 py-1 bg-gold text-dark text-xs font-semibold flex items-center gap-1">
                        <Star className="w-3 h-3" /> COUVERTURE
                      </span>
                    )}
                    
                    {/* Hover overlay with actions */}
                    <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                      <Eye className="w-6 h-6 text-white mb-2" />
                      
                      {!isCover && allMedia.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAlbumCover(media.id, media.url);
                          }}
                          className="flex items-center gap-1 px-3 py-1.5 bg-gold text-dark text-xs font-semibold hover:bg-gold-light transition-colors rounded"
                        >
                          <Star className="w-3 h-3" />
                          Définir couverture
                        </button>
                      )}
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          removeMedia(media.id);
                        }}
                        className="flex items-center gap-1 px-3 py-1.5 bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors rounded"
                      >
                        <Trash2 className="w-3 h-3" />
                        Supprimer
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {allMedia.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <Image className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun média dans cet album</p>
            <p className="text-sm">Utilisez la zone ci-dessus pour ajouter vos photos et vidéos</p>
          </div>
        )}

        {/* Media Viewer Modal */}
        <Dialog open={selectedMedia !== null} onOpenChange={(open) => !open && closeMediaModal()}>
          <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden p-0">
            {selectedMedia && (
              <div className="flex flex-col">
                <div className="relative bg-black flex items-center justify-center min-h-[400px]">
                  {isVideo(selectedMedia) ? (
                    <video
                      ref={videoRef}
                      src={`${getImageUrl(selectedMedia.url)}`}
                      className="max-w-full max-h-[70vh] object-contain"
                      controls
                      autoPlay
                    />
                  ) : (
                    <img
                      src={`${getImageUrl(selectedMedia.url)}`}
                      alt=""
                      className="max-w-full max-h-[70vh] object-contain"
                    />
                  )}
                </div>
                <div className="p-4 border-t border-white/10 flex items-center justify-between">
                  <span className="text-sm text-gray-400 flex items-center gap-2">
                    {isVideo(selectedMedia) ? <Video className="w-4 h-4" /> : <Image className="w-4 h-4" />}
                    {isVideo(selectedMedia) ? 'Vidéo' : 'Photo'}
                  </span>
                  <Button
                    onClick={() => removeMedia(selectedMedia.id)}
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Supprimer
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Album Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="bg-dark-paper border-white/10">
            <DialogHeader>
              <DialogTitle className="text-gold">Modifier l'album</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-white">Nom de l'album</Label>
                <Input
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="bg-dark-subtle border-white/10 text-white mt-1"
                />
              </div>
              <div className="flex items-center justify-between p-4 glass-card">
                <div className="flex items-center gap-2">
                  {editPrivate ? (
                    <Lock className="w-5 h-5 text-gold" />
                  ) : (
                    <Unlock className="w-5 h-5 text-green-500" />
                  )}
                  <span className="text-white">{editPrivate ? 'Album privé' : 'Album public'}</span>
                </div>
                <Switch
                  checked={editPrivate}
                  onCheckedChange={setEditPrivate}
                />
              </div>
              <p className="text-xs text-gray-500">
                {editPrivate 
                  ? 'Seuls les membres ayant accès à vos photos privées pourront voir cet album.'
                  : 'Cet album sera visible par tous les membres abonnés.'}
              </p>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditModal(false)}>
                Annuler
              </Button>
              <Button onClick={updateAlbum} className="btn-gold">
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Render media grid (photos + videos) - Always public
  const renderMediaGrid = (photos, videos) => {
    const allMedia = [
      ...(photos || []).map(p => ({ ...p, type: 'photo' })),
      ...(videos || []).map(v => ({ ...v, type: 'video' }))
    ];

    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {/* Upload Buttons */}
        <label className="aspect-square border-2 border-dashed border-white/20 rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-gold/50 transition-colors">
          {uploading ? (
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          ) : (
            <>
              <Image className="w-6 h-6 text-gray-500 mb-1" />
              <span className="text-xs text-gray-500">Photo</span>
            </>
          )}
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handlePhotoUpload(e, false)}
            className="hidden"
            disabled={uploading}
          />
        </label>
        
        <label className="aspect-square border-2 border-dashed border-white/20 rounded-sm flex flex-col items-center justify-center cursor-pointer hover:border-gold/50 transition-colors">
          {uploading ? (
            <div className="w-8 h-8 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
          ) : (
            <>
              <Video className="w-6 h-6 text-gray-500 mb-1" />
              <span className="text-xs text-gray-500">Vidéo</span>
            </>
          )}
          <input
            type="file"
            accept="video/*"
            onChange={(e) => handleVideoUpload(e, false)}
            className="hidden"
            disabled={uploading}
          />
        </label>

        {allMedia.map((media, index) => {
          const isCurrentProfilePic = profile?.profile_picture === media.url;
          return (
            <div 
              key={media.id || index}
              className="aspect-square relative group cursor-pointer rounded-sm overflow-hidden bg-dark-subtle"
              onClick={() => openMediaModal(media)}
            >
              {media.type === 'video' || isVideo(media) ? (
                <>
                  <video
                    src={`${getImageUrl(media.url)}`}
                    className="w-full h-full object-cover"
                    muted
                    preload="metadata"
                  />
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Play className="w-10 h-10 text-white" />
                  </div>
                  <div className="absolute top-2 left-2">
                    <Video className="w-4 h-4 text-gold" />
                  </div>
                </>
              ) : (
                <>
                  <img
                    src={`${getImageUrl(media.url)}`}
                    alt=""
                    className={`w-full h-full object-cover ${isCurrentProfilePic ? 'ring-2 ring-gold' : ''}`}
                  />
                  {isCurrentProfilePic && (
                    <span className="absolute top-2 left-2 px-2 py-1 bg-gold text-dark text-xs font-semibold">
                      PROFIL
                    </span>
                  )}
                </>
              )}
              
              {/* Overlay */}
              <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                <Eye className="w-6 h-6 text-white mb-1" />
                {media.type !== 'video' && !isVideo(media) && !isCurrentProfilePic && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setAsProfilePicture(media.id, media.url); }}
                    className="flex items-center gap-1 px-2 py-1 bg-gold text-dark text-xs font-semibold hover:bg-gold-light transition-colors rounded"
                  >
                    <UserCircle className="w-3 h-3" />
                    Profil
                  </button>
                )}
                {/* Delete button */}
                <button
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (media.type === 'video' || isVideo(media)) {
                      deleteVideo(media.id);
                    } else {
                      deletePhoto(media.id);
                    }
                  }}
                  className="flex items-center gap-1 px-2 py-1 bg-red-500 text-white text-xs font-semibold hover:bg-red-600 transition-colors rounded"
                  data-testid={`delete-media-${media.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                  Supprimer
                </button>
              </div>
              
              {/* Likes count */}
              <div className="absolute bottom-2 left-2">
                <span className="flex items-center gap-1 bg-black/50 px-2 py-1 rounded text-xs text-white">
                  <Heart className="w-3 h-3" /> {media.likes || 0}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Main view - Single page layout: Photos/Videos then Albums
  return (
    <div data-testid="gallery-manager">
      {/* Section 1: Photos et Vidéos (always public) */}
      <div className="mb-10">
        <h3 className="text-gold text-sm uppercase tracking-widest mb-4 flex items-center gap-2">
          <Image className="w-4 h-4" /> Photos et Vidéos
        </h3>
        {renderMediaGrid(profile?.photos, profile?.videos)}
      </div>

      {/* Section 2: Albums (can be public or private) */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-gold text-sm uppercase tracking-widest flex items-center gap-2">
            <FolderPlus className="w-4 h-4" /> Albums
          </h3>
          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="btn-gold" data-testid="create-album-btn">
                <FolderPlus className="w-4 h-4 mr-2" /> Créer un album
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-dark-paper border-white/10">
              <DialogHeader>
                <DialogTitle className="text-gold">Créer un nouvel album</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label className="text-white">Nom de l'album</Label>
                  <Input
                    value={newAlbumName}
                    onChange={(e) => setNewAlbumName(e.target.value)}
                    placeholder="Ex: Vacances d'été"
                    className="bg-dark-subtle border-white/10 text-white mt-1"
                  />
                </div>
                <div className="flex items-center justify-between p-4 glass-card">
                  <div className="flex items-center gap-2">
                    {newAlbumPrivate ? (
                      <Lock className="w-5 h-5 text-gold" />
                    ) : (
                      <Unlock className="w-5 h-5 text-green-500" />
                    )}
                    <span className="text-white">{newAlbumPrivate ? 'Album privé' : 'Album public'}</span>
                  </div>
                  <Switch
                    checked={newAlbumPrivate}
                    onCheckedChange={setNewAlbumPrivate}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  {newAlbumPrivate 
                    ? 'Seuls les membres ayant accès à vos contenus privés pourront voir cet album.'
                    : 'Cet album sera visible par tous les membres abonnés.'}
                </p>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Annuler
                </Button>
                <Button onClick={createAlbum} className="btn-gold">
                  Créer l'album
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {albums.length === 0 ? (
          <div className="glass-card p-12 text-center">
            <FolderPlus className="w-12 h-12 mx-auto text-gray-500 mb-4" />
            <p className="text-gray-400 mb-4">Vous n'avez pas encore d'album</p>
            <p className="text-sm text-gray-500 mb-6">
              Créez des albums pour organiser vos photos et vidéos
            </p>
            <Button onClick={() => setShowCreateModal(true)} className="btn-gold">
              <FolderPlus className="w-4 h-4 mr-2" /> Créer mon premier album
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {albums.map((album) => {
              const mediaCount = (album.media?.length || 0) + (album.photos?.length || 0);
              return (
                <div 
                  key={album.id}
                  className="glass-card overflow-hidden group cursor-pointer hover:border-gold/30 transition-colors"
                  onClick={() => fetchAlbumDetails(album.id)}
                  data-testid={`album-card-${album.id}`}
                >
                  <div className="aspect-video bg-dark-subtle relative">
                    {album.cover_url || album.cover_photo_url ? (
                      <img
                        src={`${getImageUrl(album.cover_url || album.cover_photo_url)}`}
                        alt={album.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <FolderPlus className="w-12 h-12 text-gray-600" />
                      </div>
                    )}
                    <div className="absolute top-2 right-2">
                      {album.is_private ? (
                        <span className="px-2 py-1 bg-black/60 rounded text-xs text-gold flex items-center gap-1">
                          <Lock className="w-3 h-3" /> Privé
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-black/60 rounded text-xs text-green-500 flex items-center gap-1">
                          <Unlock className="w-3 h-3" /> Public
                        </span>
                      )}
                    </div>
                    <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 bg-black/60 hover:bg-black/80">
                            <MoreVertical className="w-4 h-4 text-white" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-dark-paper border-white/10">
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchAlbumDetails(album.id).then(() => openEditModal(album));
                            }}
                            className="text-white hover:bg-white/10"
                          >
                            <Edit2 className="w-4 h-4 mr-2" /> Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteAlbum(album.id);
                            }}
                            className="text-red-400 hover:bg-red-500/10"
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="font-semibold text-white truncate">{album.name}</h3>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <Image className="w-3 h-3" />
                      {mediaCount} média{mediaCount !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Media Viewer Modal */}
      <Dialog open={selectedMedia !== null} onOpenChange={(open) => !open && closeMediaModal()}>
        <DialogContent className="bg-dark-paper border-white/10 max-w-4xl max-h-[90vh] overflow-hidden p-0">
          {selectedMedia && (
            <div className="flex flex-col">
              <div className="relative bg-black flex items-center justify-center min-h-[400px]">
                {selectedMedia.type === 'video' || isVideo(selectedMedia) ? (
                  <video
                    ref={videoRef}
                    src={`${getImageUrl(selectedMedia.url)}`}
                    className="max-w-full max-h-[70vh] object-contain"
                    controls
                    autoPlay
                  />
                ) : (
                  <img
                    src={`${getImageUrl(selectedMedia.url)}`}
                    alt=""
                    className="max-w-full max-h-[70vh] object-contain"
                  />
                )}
              </div>
              <div className="p-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-sm text-gray-400 flex items-center gap-2">
                  {selectedMedia.type === 'video' || isVideo(selectedMedia) ? (
                    <><Video className="w-4 h-4" /> Vidéo</>
                  ) : (
                    <><Image className="w-4 h-4" /> Photo</>
                  )}
                  <span className="text-gray-600">•</span>
                  <Heart className="w-4 h-4" /> {selectedMedia.likes || 0} likes
                </span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
